import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import PublicNav from "../components/PublicNav";
import SeoExpandable from "../components/SeoExpandable";

const PURPLE = "#312e81";

function ChartsPage() {
  const navigate = useNavigate();
  const [overview, setOverview] = useState({ markets: [], starline_markets: [] });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const prev = document.title;
    document.title = "Satta Matka Charts | Kalyan, Milan & Rajdhani Jodi Charts";
    const desc = document.querySelector("meta[name='description']");
    const prevDesc = desc?.getAttribute("content") || "";
    desc?.setAttribute("content", "Explore the latest kalyan matka charts, milan charts, panel charts, jodi charts, and historical satta matka results updated regularly.");
    return () => {
      document.title = prev;
      if (desc) desc.setAttribute("content", prevDesc);
    };
  }, []);

  useEffect(() => {
    setLoading(true);
    API.get("/charts/overview")
      .then((res) => setOverview(res.data || { markets: [], starline_markets: [] }))
      .catch(() => setOverview({ markets: [], starline_markets: [] }))
      .finally(() => setLoading(false));
  }, []);

  const markets = useMemo(() => overview.markets || [], [overview]);
  const starlineMarkets = useMemo(() => overview.starline_markets || [], [overview]);

  return (
    <div style={page}>
      <PublicNav />
      <h1 style={pageTitle}>Latest Satta Matka Game Charts &amp; Live Results</h1>

      <main style={main}>
        {loading ? (
          <p style={loadingTxt}>Loading charts…</p>
        ) : (
          <>
            {starlineMarkets.length > 0 && (
              <section style={section}>
                <div style={sectionHdr}>STARLINE CHARTS</div>
                <div style={btnGrid}>
                  {starlineMarkets.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      style={mktBtn}
                      onClick={() => navigate(`/charts/starline/${m.id}`)}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {markets.length > 0 && (
              <section style={section}>
                <div style={sectionHdr}>PANNA CHARTS</div>
                <div style={btnGrid}>
                  {markets.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      style={mktBtn}
                      onClick={() => navigate(`/charts/market/${m.id}?view=panel`)}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {markets.length > 0 && (
              <section style={section}>
                <div style={sectionHdr}>JODI CHARTS</div>
                <div style={btnGrid}>
                  {markets.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      style={mktBtn}
                      onClick={() => navigate(`/charts/market/${m.id}?view=jodi`)}
                    >
                      {m.name}
                    </button>
                  ))}
                </div>
              </section>
            )}

            {!markets.length && !starlineMarkets.length && (
              <p style={emptyTxt}>No chart data available yet.</p>
            )}
          </>
        )}
      </main>
      <SeoExpandable title="Understanding Historical Number Charts" sections={CHARTS_SEO_SECTIONS} faqs={CHARTS_SEO_FAQS} />
    </div>
  );
}

const CHARTS_SEO_SECTIONS = [
  { heading: `What Are Historical Number Charts?`, body: `Historical number charts are records that organize previously published results in a structured format. These charts are commonly arranged by date, draw session, or game name, making it easier for readers to review past outcomes and understand how results have been recorded over time.

The primary purpose of historical charts is documentation. They provide an archive of previous results that can be referenced for educational, historical, or research purposes. Maintaining accurate historical records helps readers access information in an organized and easy-to-read format.

How to Read Historical Charts

Historical charts generally present information in chronological order. Depending on the format used, they may include:

Date of the recorded result

Name of the game or draw

Published outcome

Opening and closing information (where applicable)

Archived historical records

Understanding how information is organized allows readers to locate specific historical entries more efficiently.

Why Historical Records Are Maintained

Historical records serve several informational purposes:

Preserving previously published results

Providing a chronological archive

Supporting educational discussions about gambling history

Helping readers understand how historical information is presented

Maintaining transparency in published records

These records should be viewed as historical documentation rather than predictive tools.

Can Historical Charts Predict Future Results?

No. Historical charts cannot reliably predict future outcomes.

Games of chance are generally based on independent random events. This means that previous outcomes do not influence future ones. Reviewing historical records may help readers understand how results were recorded in the past, but it does not provide a reliable method for forecasting future outcomes.

Understanding Random Outcomes

Random events operate independently. Every eligible outcome has its own mathematical probability according to the rules of the game.

A common misconception is that a result becomes more likely simply because it has not appeared recently. This belief is known as the gambler's fallacy. In reality, independent random events do not &quot;remember&quot; previous outcomes.

Responsible Use of Historical Information

Historical records should be used only for informational or educational purposes. Anyone researching gambling-related topics should understand:

Past results do not guarantee future outcomes.

Gambling always involves financial risk.

Probability cannot eliminate uncertainty.

Gambling should never be viewed as a reliable source of income.

Understanding these principles can help readers develop realistic expectations and make informed decisions.` },
  { heading: `Why Historical Records Matter`, body: `Historical records provide a chronological archive of previously published results, making it easier for readers to review information from different dates and understand how records have been maintained over time. Organized historical data can also be useful for educational discussions about probability, randomness, and the history of gambling-related activities.

How Results Are Organized

Historical records are typically arranged in a structured format to improve readability and accessibility. Depending on the website, records may be organized by:

Date

Draw session

Game or category

Opening and closing information (where applicable)

Chronological order

This organization allows readers to locate historical information quickly and efficiently.

Importance of Accurate Record Keeping

Maintaining accurate historical records helps preserve information and improves transparency. Clear record keeping allows readers to review previously published information while reducing confusion caused by incomplete or inconsistent data.

Good record management also supports educational research and historical reference.

Understanding Independent Events

One of the most important concepts in probability is that independent events do not influence one another. In games of chance, each eligible outcome has its own probability regardless of what happened in previous events.

This is why historical records should be viewed as documentation rather than evidence that future outcomes can be predicted.

Probability and Randomness

Probability explains the mathematical likelihood of different outcomes, while randomness describes events that occur without predictable patterns.

Although historical records may show repeated or infrequent outcomes over time, these observations do not change the probability of future independent events.

Why Patterns Can Be Misleading

People naturally look for patterns in historical information. However, random events often produce sequences that appear meaningful even when they occur purely by chance.

Recognizing this helps readers better understand why historical data should not be used as a reliable forecasting tool.

Educational Purpose of Historical Information

Historical records can support learning in several ways:

Understanding probability concepts.

Learning about randomness.

Exploring the history of gambling-related activities.

Studying how information is archived.

Improving general awareness of responsible gambling.

These educational purposes differ from using historical information to make predictions.

Responsible Decision-Making

If gambling is legal where an individual lives, informed decision-making is essential. Responsible practices include understanding financial risks, setting personal limits, recognizing warning signs, and treating gambling as entertainment rather than a source of income.

Learning about these principles helps reduce misconceptions and encourages healthier decision-making.

Our Commitment to Accurate Information

We strive to present historical information in a clear and organized manner while emphasizing accuracy and educational value. When appropriate, we review and update content to improve clarity and reflect changes in publicly available information.

Disclaimer

All historical information provided on this website is intended for educational and informational purposes only. Historical records document past events and should not be interpreted as predictions, guarantees, strategies, or financial advice. Readers are encouraged to understand the laws that apply in their jurisdiction and to practice responsible gambling if they choose to participate where it is legal.

### 1. What are historical number charts?

Historical number charts are records of previously published results that are organized by date, draw, or game. They are maintained for informational and reference purposes.

### 2. Why are historical charts published?

Historical charts provide an archive of past results, allowing readers to review previously published information and understand how records have been maintained over time.

### 3. Can historical charts predict future outcomes?

No. Historical charts document past events only and cannot reliably predict future outcomes in games of chance.

### 4. Why do people review historical records?

People may review historical records for educational purposes, research, or to understand how results have been presented historically.

### 5. What information is typically included in a historical chart?

Depending on the format, a historical chart may include dates, draw sessions, game names, and previously published results.

### 6. How are historical charts organized?

Most historical charts are arranged chronologically by date or draw session, making it easier to locate specific records.

### 7. Are all historical charts the same?

No. The layout and information included in historical charts may differ depending on the website or publication.

### 8. What does independent probability mean?

Independent probability means that each new event has its own chance of occurring and is not affected by previous outcomes.

### 9. What is the gambler's fallacy?

The gambler's fallacy is the mistaken belief that past random outcomes influence future independent events. In reality, each independent event starts with its own probability.

### 10. Why is randomness important in games of chance?

Randomness means that outcomes occur without predictable patterns, making future results uncertain.

### 11. Can patterns in historical records guarantee future results?

No. Apparent patterns in historical records do not provide reliable evidence for predicting future outcomes.

### 12. Why is probability important to understand?

Probability explains the likelihood of different outcomes and helps readers understand why chance-based events cannot be predicted with certainty.

### 13. Are historical records useful for educational purposes?

Yes. Historical records can help explain concepts such as randomness, probability, record keeping, and the history of gambling-related activities.

### 14. Do gambling laws vary by location?

Yes. Gambling laws differ between countries and may also vary by state, province, or region. Individuals should understand the laws that apply where they live.

### 15. What is responsible gambling?

Responsible gambling means understanding the risks, setting limits on spending and time, and treating gambling as entertainment rather than a way to make money.

### 16. What are some common risks associated with gambling?

Potential risks include financial loss, emotional stress, debt, relationship difficulties, and gambling-related harm.

### 17. Why is financial responsibility important?

Setting budgets, avoiding borrowed money, and keeping gambling separate from essential expenses can help reduce financial risk.

### 18. What should someone do if gambling becomes difficult to control?

If gambling begins affecting finances, relationships, work, or well-being, it is advisable to seek help from a qualified healthcare professional or a local support organization.

### 19. Why is accurate record keeping important?

Accurate historical records improve transparency, preserve information, and provide readers with reliable archives for educational and reference purposes.

### 20. What is the purpose of this page?

This page is intended to provide educational information and historical records. It should not be interpreted as advice, predictions, or encouragement to participate in gambling activities.` },
];

const CHARTS_SEO_FAQS = [
  { q: "What are historical number charts?", a: "Historical number charts are records of previously published results that are organized by date, draw, or game. They are maintained for reference and educational purposes." },
  { q: "Why are historical charts published?", a: "Historical charts provide an archive of past results, allowing readers to review previously published information and understand how results have been presented over time." },
  { q: "Can historical charts predict future outcomes?", a: "No. Historical charts document past events only and cannot reliably predict future outcomes in games of chance." },
  { q: "What information is typically included in a historical chart?", a: "Depending on the format, a historical chart may include dates, draw sessions, game names, and previously published results in chronological order." },
  { q: "What does independent probability mean?", a: "Independent probability means that each new event has its own chance of occurring and is not affected by previous outcomes." },
  { q: "What is the gambler's fallacy?", a: "The gambler's fallacy is the mistaken belief that past random outcomes influence future independent events. In reality, each result is independent." },
  { q: "Can patterns in historical records guarantee future results?", a: "No. Apparent patterns in historical records do not provide reliable evidence for predicting future outcomes." },
  { q: "Are historical records useful for educational purposes?", a: "Yes. Historical records can help explain concepts such as randomness, probability, record keeping, and the history of gambling-related activities." },
  { q: "Do gambling laws vary by location?", a: "Yes. Gambling laws differ between countries and may also vary by state, province, or region. Individuals should understand the laws applicable in their jurisdiction." },
  { q: "What is responsible gambling?", a: "Responsible gambling means understanding the risks, setting limits on spending and time, and treating gambling as entertainment rather than a source of income." },
];

export default ChartsPage;

const page = {
  minHeight: "100vh",
  background: "#ffffff",
  fontFamily: "Arial, Helvetica, sans-serif",
  color: "#0f172a",
};

const pageTitle = {
  margin: "16px 12px 4px",
  fontSize: 18,
  fontWeight: 900,
  color: PURPLE,
  textAlign: "center",
};

const main = {
  maxWidth: 960,
  margin: "0 auto",
  padding: "16px 12px 40px",
};

const section = { marginBottom: 8 };

const sectionHdr = {
  background: PURPLE,
  color: "#fff",
  fontWeight: 900,
  fontSize: 14,
  letterSpacing: "0.1em",
  padding: "11px 16px",
  marginBottom: 10,
};

const btnGrid = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(130px, 1fr))",
  gap: 8,
  marginBottom: 16,
};

const mktBtn = {
  padding: "11px 8px",
  border: "1.5px solid " + PURPLE,
  borderRadius: 6,
  background: "#fff",
  color: PURPLE,
  fontWeight: 800,
  fontSize: 11,
  cursor: "pointer",
  textAlign: "center",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  lineHeight: 1.3,
};

const loadingTxt = { textAlign: "center", padding: 32, color: PURPLE, fontWeight: 700 };
const emptyTxt   = { textAlign: "center", padding: 32, color: "#64748b", fontWeight: 700 };
